import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../services/api';
import Appointments from './Appointments';
import IntakeDashboard from './IntakeDashboard';
import ConsentManagement from './ConsentManagement';

const TABS = [
  { key: 'calendar', label: 'Calendar' },
  { key: 'intake', label: 'Intake Center' },
  { key: 'consent', label: 'Consent' },
];

function CareHub({ practiceId }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'calendar');
  const [intakeSetupDismissed, setIntakeSetupDismissed] = useState(false);
  const [intakePackageCount, setIntakePackageCount] = useState(null);
  const [importingTemplate, setImportingTemplate] = useState(false);
  const [intakeTemplates, setIntakeTemplates] = useState([]);

  // GH-142: Pre-Visit Readiness → Open in Care Hub deep-links here as
  //   /practice-view/care-hub?appointment_id=X
  // Force the calendar tab and pass the id down so Appointments can
  // auto-select the matching row. Numeric-string coercion tolerates both
  // ?appointment_id=42 and legacy encoded forms.
  const deepLinkApptId = (() => {
    const raw = searchParams.get('appointment_id');
    return raw ? String(raw) : null;
  })();

  useEffect(() => {
    if (deepLinkApptId && activeTab !== 'calendar') {
      setActiveTab('calendar');
    }
    // Ensure the tab param is written so the URL is canonical after the deep-link
    const tab = searchParams.get('tab');
    if (tab && TABS.some(t => t.key === tab)) {
      setActiveTab(tab);
    }
  }, [searchParams, deepLinkApptId, activeTab]);

  // Check intake package count when intake tab is active with setup flag
  useEffect(() => {
    if (!practiceId || activeTab !== 'intake') return;
    const check = async () => {
      try {
        const [pkgRes, tplRes] = await Promise.all([
          api.getIntakePackages(practiceId),
          api.getIntakeTemplates(practiceId),
        ]);
        setIntakePackageCount((pkgRes.data || []).length);
        setIntakeTemplates(tplRes.data || []);
      } catch { /* ignore */ }
    };
    check();
  }, [practiceId, activeTab]);

  const handleImportTemplate = useCallback(async (templateId) => {
    try {
      setImportingTemplate(true);
      await api.importIntakeTemplate(practiceId, templateId);
      setIntakeSetupDismissed(true);
      setSearchParams({ tab: 'intake' });
      // Refresh count
      const pkgRes = await api.getIntakePackages(practiceId);
      setIntakePackageCount((pkgRes.data || []).length);
    } catch (err) {
      alert(`Import failed: ${err.message}`);
    } finally {
      setImportingTemplate(false);
    }
  }, [practiceId, setSearchParams]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  const showIntakeSetup = searchParams.get('setup') === 'true'
    && activeTab === 'intake'
    && intakePackageCount === 0
    && !intakeSetupDismissed;

  if (!practiceId) {
    return (
      <div style={styles.container}>
        <p style={{ color: '#6b7280' }}>Please select a practice from the dropdown above.</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>Care Hub</h2>
      </div>

      <div style={styles.tabBar}>
        {TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => handleTabChange(tab.key)}
            style={{
              ...styles.tab,
              ...(activeTab === tab.key ? styles.tabActive : {})
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div style={styles.tabBody}>
        {activeTab === 'calendar' && (
          <Appointments practiceId={practiceId} embedded initialSelectedApptId={deepLinkApptId} />
        )}
        {activeTab === 'intake' && (
          <>
            {showIntakeSetup && (
              <div style={setupStyles.banner}>
                <div style={setupStyles.iconWrap}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M16 4h2a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2h2" /><rect x="8" y="2" width="8" height="4" rx="1" />
                  </svg>
                </div>
                <div style={{ flex: 1 }}>
                  <h3 style={setupStyles.title}>Set Up Intake Forms</h3>
                  <p style={setupStyles.desc}>
                    Import a predefined intake template to start collecting patient information digitally.
                  </p>
                  {intakeTemplates.length > 0 && (
                    <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                      {intakeTemplates.slice(0, 4).map(t => (
                        <button
                          key={t.id}
                          style={setupStyles.templateBtn}
                          onClick={() => handleImportTemplate(t.id)}
                          disabled={importingTemplate}
                        >
                          {t.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div style={setupStyles.actions}>
                  <button style={setupStyles.dismissBtn} onClick={() => { setIntakeSetupDismissed(true); setSearchParams({ tab: 'intake' }); }}>
                    Dismiss
                  </button>
                </div>
              </div>
            )}
            <IntakeDashboard practiceId={practiceId} />
          </>
        )}
        {activeTab === 'consent' && <ConsentManagement practiceId={practiceId} />}
      </div>
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100vh - 64px)',
    overflow: 'hidden',
    background: '#f8fafc',
  },
  header: {
    padding: '22px 24px 0 24px',
  },
  title: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#0f172a',
    margin: 0,
    fontFamily: "'JetBrains Mono', monospace",
    letterSpacing: '-0.5px',
  },
  subtitle: {
    fontSize: '11px',
    color: '#94a3b8',
    textTransform: 'uppercase',
    letterSpacing: '1.5px',
    marginTop: '2px',
    fontWeight: '500',
  },
  tabBar: {
    display: 'flex',
    gap: '0',
    borderBottom: '1px solid #e2e8f0',
    padding: '18px 24px 0 24px',
  },
  tab: {
    padding: '8px 16px 13px',
    border: 'none',
    background: 'transparent',
    fontSize: '12px',
    fontWeight: '600',
    color: '#94a3b8',
    cursor: 'pointer',
    borderBottom: '2px solid transparent',
    marginBottom: '-1px',
    transition: 'color 0.2s ease',
    fontFamily: "'JetBrains Mono', monospace",
    letterSpacing: '0.3px',
  },
  tabActive: {
    color: '#2563eb',
    borderBottomColor: '#2563eb',
  },
  tabBody: {
    flex: 1,
    overflow: 'auto',
  },
};

const setupStyles = {
  banner: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 16,
    padding: '16px 20px',
    margin: '16px 24px 0',
    backgroundColor: '#eff6ff',
    border: '1px solid #bfdbfe',
    borderRadius: 8,
  },
  iconWrap: {
    width: 44,
    height: 44,
    borderRadius: 10,
    backgroundColor: '#dbeafe',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  title: { margin: 0, fontSize: 15, fontWeight: 600, color: '#1e40af' },
  desc: { margin: '4px 0 0', fontSize: 13, color: '#3b82f6', lineHeight: 1.4 },
  actions: { display: 'flex', gap: 8, flexShrink: 0 },
  templateBtn: {
    padding: '5px 12px',
    backgroundColor: '#3b82f6',
    color: '#fff',
    border: 'none',
    borderRadius: 5,
    fontSize: 12,
    fontWeight: 500,
    cursor: 'pointer',
  },
  dismissBtn: {
    padding: '8px 14px',
    backgroundColor: 'transparent',
    color: '#6b7280',
    border: '1px solid #d1d5db',
    borderRadius: 6,
    fontSize: 13,
    cursor: 'pointer',
  },
};

export default CareHub;
